import { useState } from 'react'
import { useCalendar } from '../hooks/useCalendar'

const CalendarApp = () => {
  const { 
    events, 
    loading, 
    error, 
    selectedDate, 
    changeDate, 
    nextMonth, 
    previousMonth, 
    goToToday 
  } = useCalendar()
  
  const [selectedDay, setSelectedDay] = useState(null)
  const [activeTab, setActiveTab] = useState('selected')
  
  // Get days in current month
  const getDaysInMonth = () => {
    const year = selectedDate.getFullYear()
    const month = selectedDate.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = firstDay.getDay()
    
    const days = []
    
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null)
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day)
    }
    
    return days
  }
  
  const handleDayClick = (day) => {
    if (day) {
      const newDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), day)
      setSelectedDay(day)
      changeDate(newDate)
    }
  }
  
  const formatMonthYear = () => {
    return selectedDate.toLocaleDateString('en-US', { 
      month: 'long', 
      year: 'numeric' 
    })
  }
  
  const isToday = (day) => {
    const today = new Date()
    return day && 
           today.getFullYear() === selectedDate.getFullYear() &&
           today.getMonth() === selectedDate.getMonth() &&
           today.getDate() === day
  }
  
  const isSelectedDay = (day) => {
    return day && 
           selectedDate.getFullYear() === selectedDate.getFullYear() &&
           selectedDate.getMonth() === selectedDate.getMonth() &&
           selectedDate.getDate() === day
  }
  
  const renderEventContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-40">
          <div className="flex items-center space-x-2">
            <i className="fas fa-spinner fa-spin"></i>
            <span>Loading historical events...</span>
          </div>
        </div>
      )
    }
    
    if (error) {
      return (
        <div className="text-center text-red-400 p-4">
          <i className="fas fa-exclamation-triangle mb-2"></i>
          <p>Error loading events: {error}</p>
        </div>
      )
    }
    
    if (!events || !events[activeTab] || events[activeTab].length === 0) {
      return (
        <div className="text-center text-white/60 p-8">
          <i className="fas fa-calendar-day text-4xl mb-4"></i>
          <p>No {activeTab} events found for this date.</p>
        </div>
      )
    }
    
    return (
      <div className="space-y-4">
        {events[activeTab].slice(0, 10).map((event, index) => (
          <div key={index} className="glass-effect rounded-lg p-4 hover:bg-white/5 transition-colors">
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                {activeTab === 'births' ? '🎂' : 
                 activeTab === 'deaths' ? '🕊️' :
                 activeTab === 'holidays' ? '🎉' : '📜'}
              </div>
              <div className="flex-1">
                {event.year && (
                  <span className="inline-block bg-purple-500/20 text-purple-300 px-2 py-1 rounded text-xs mb-2">
                    {event.year > 0 ? `${event.year} AD` : `${Math.abs(event.year)} BC`}
                  </span>
                )}
                <p className="text-sm leading-relaxed">
                  {event.text || event.description}
                </p>
                {event.person && (
                  <p className="text-xs text-pink-400 mt-1 font-medium">
                    {event.person}
                  </p>
                )}
                {event.pages && event.pages.length > 0 && event.pages[0].extract && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs text-blue-400 hover:text-blue-300">
                      Learn more
                    </summary>
                    <p className="text-xs text-white/70 mt-2 pl-4 border-l-2 border-blue-500/30">
                      {event.pages[0].extract.slice(0, 200)}...
                    </p>
                  </details>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }
  
  const getTabCount = (tab) => {
    return events && events[tab] ? events[tab].length : 0
  }
  
  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center p-6 border-b border-white/10">
        <h1 className="text-2xl font-bold flex items-center">
          <i className="fas fa-calendar-day mr-3 text-purple-400"></i>
          On This Day
        </h1>
        <div className="flex items-center space-x-2">
          <button 
            onClick={previousMonth}
            className="px-3 py-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg transition-colors"
          >
            <i className="fas fa-chevron-left"></i>
          </button>
          <span className="px-4 py-2 text-lg font-semibold min-w-[180px] text-center">
            {formatMonthYear()}
          </span>
          <button 
            onClick={nextMonth}
            className="px-3 py-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg transition-colors"
          >
            <i className="fas fa-chevron-right"></i>
          </button>
          <button 
            onClick={goToToday}
            className="px-3 py-2 bg-purple-500/20 hover:bg-purple-500/30 rounded-lg transition-colors text-sm ml-2"
          >
            Today
          </button>
        </div>
      </div>
      
      <div className="flex-1 flex overflow-hidden">
        {/* Calendar Grid */}
        <div className="w-1/2 p-6 border-r border-white/10">
          <div className="grid grid-cols-7 gap-1">
            {/* Days of week */}
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="p-2 text-center font-semibold text-white/70 text-sm">
                {day}
              </div>
            ))}
            
            {/* Calendar dates */}
            {getDaysInMonth().map((day, index) => (
              <div 
                key={index}
                onClick={() => handleDayClick(day)}
                className={`
                  p-2 text-center text-sm rounded cursor-pointer transition-colors relative
                  ${!day ? '' : 'hover:bg-white/10'}
                  ${isToday(day) ? 'bg-purple-500/30 text-purple-200 font-bold' : ''}
                  ${isSelectedDay(day) ? 'bg-blue-500/30 text-blue-200 ring-1 ring-blue-400/50' : ''}
                `}
              >
                {day}
                {isToday(day) && (
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-purple-400 rounded-full"></div>
                )}
              </div>
            ))}
          </div>
          
          <div className="mt-4 text-center text-sm text-white/60">
            <p>Selected: {selectedDate.toLocaleDateString('en-US', { 
              weekday: 'long',
              month: 'long', 
              day: 'numeric',
              year: 'numeric'
            })}</p>
          </div>
        </div>
        
        {/* Events Panel */}
        <div className="w-1/2 flex flex-col">
          {/* Event Tabs */}
          <div className="flex border-b border-white/10">
            {[
              { key: 'selected', label: 'Featured', icon: '✨' },
              { key: 'events', label: 'Events', icon: '📜' },
              { key: 'births', label: 'Births', icon: '🎂' },
              { key: 'deaths', label: 'Deaths', icon: '🕊️' },
              { key: 'holidays', label: 'Holidays', icon: '🎉' }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`
                  flex-1 px-3 py-3 text-sm font-medium transition-colors relative
                  ${activeTab === tab.key 
                    ? 'text-purple-300 border-b-2 border-purple-400' 
                    : 'text-white/70 hover:text-white/90'
                  }
                `}
              >
                <span className="mr-1">{tab.icon}</span>
                {tab.label}
                {getTabCount(tab.key) > 0 && (
                  <span className="ml-1 bg-white/20 px-1.5 py-0.5 rounded-full text-xs">
                    {getTabCount(tab.key)}
                  </span>
                )}
              </button>
            ))}
          </div>
          
          {/* Event Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {renderEventContent()}
          </div>
        </div>
      </div>
    </div>
  )
}

export default CalendarApp
